import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactGA from "react-ga4";

import {
  setFilledCompany,
  setClaimTradeLicenseDetails,
  setIndustryDetails,
  setClaimAddressDetails,
  setClearClaimDetails,
} from "../../store/claim/claimSlice";
import { setCompany } from "../../store/search/searchSlice";
import { setUserInfo } from "../../store/auth/authSlice";
import { useGetUserDetailsMutation } from "../../store/auth/usersApiSlice";
import {
  useGetSubIndustriesDataQuery,
  useGetCompanyClaimedMutation,
} from "../../store/Company/companyApiSlice";
import useCompanyData from "../useCompanyData";
import useListBusiness from "./useListBusiness";
import { LicensingAuthorityStateWiseDetails } from "../../constants/licensingAutority";
import { getStatesByCountryCode } from "../../utils/countriesState";
import { extractCountryCodeAndPhoneCode } from "../../utils/countriesState";
import { uploadFilePath } from "../../constants/s3UploadPath";
import { uploadFileToS3 } from "../../utils/uploadFileToS3";
import { validateEmail } from "../../utils/Helper";
import {
  DEFAULT_WORKING_HOURS,
  formatWorkingHours,
  parseWorkingHours,
} from "../../components/public/ClaimBusiness/u1/workingHours";

/* The claim intake, lifted out of pages/public/Company/[claimCompany].jsx and the five components under components/public/SignUp/CompanyProfile so a... */

export const CLAIM_STEPS = ["Company", "License", "Industry", "Address", "Contact"];
export const TOTAL_STEPS = CLAIM_STEPS.length;
/* One past the last form step: the submitted confirmation. */
export const SUBMITTED_STEP = TOTAL_STEPS + 1;

const EMPTY_ADDRESS = {
  line1: "",
  line2: "",
  area: "",
  city: "",
  postalCode: "",
};

/* The number as it goes on the wire: digits only, joined to the dial code. */
export const digitsOf = (value) => String(value || "").replace(/[^0-9]/g, "");

const ga = (action, label) =>
  ReactGA.event({ category: "Claim", action: `Claim Process:${action}`, label });

export default function useClaimFlow({ id } = {}) {
  const dispatch = useDispatch();
  const { token, userInfo } = useSelector((state) => state.auth);
  const { worldBoxToken } = useSelector((state) => state.search);
  /* Written by the company-search step on /sign-up, and the
     only place that knows whether this is a brand-new business. The fetched
     company is empty on that path, so it cannot answer for companyID. */
  const { filledCompany } = useSelector((state) => state.claims);

  const { data: company, isLoading: companyLoading } = useCompanyData(
    id,
    token,
    worldBoxToken
  );
  const { data: subIndustries, isLoading: industriesLoading } =
    useGetSubIndustriesDataQuery({ token });
  const [getCompanyClaimed, { isLoading: submitting }] = useGetCompanyClaimedMutation();
  /* The viewer's own record — who they are and which companies they hold.
     The profile screen reads its role straight off it (roleOn in
     useCompanyProfile), so it has to be refreshed before they land there. */
  const [getUserDetails] = useGetUserDetailsMutation();

  const [step, setStep] = useState(1);

  /* With no company in the URL the user is listing a business they have to
     find first, so step 1 searches the directory. Arriving from a company's
     own profile, the record is already known and step 1 just confirms it. */
  const searchable = !id;
  const search = useListBusiness({ onSelected: () => setStep(2), enabled: searchable });

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  /* Set when the claim comes back refused because the company already has an
     owner — the page swaps the wizard for the dispute form (ICC-11). */
  const [blockedAsClaimed, setBlockedAsClaimed] = useState(false);
  /* The TREVEX companyID the claim comes back with. A WorldBox record is
     claimed by its WIN, and claiming it is what mints the TREVEX company —
     so the id in the URL is not the id the new profile lives at. Keeping it
     is the difference between /search/IN0016180513 and /search/TRX…. */
  const [claimedCompanyId, setClaimedCompanyId] = useState(null);
  const [uploading, setUploading] = useState(false);

  /* Step 1 */
  const [companyName, setCompanyName] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  /* Step 2 */
  const [licenseNo, setLicenseNo] = useState("");
  const [licenseState, setLicenseState] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [registrationBody, setRegistrationBody] = useState("");
  const [licenseFile, setLicenseFile] = useState(null);
  /* The uploaded licence's URL, set when step 2 is left. */
  const [licenseFileUrl, setLicenseFileUrl] = useState(null);
  /* Step 3 */
  const [industries, setIndustries] = useState([]);
  /* Step 4 */
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [workingHours, setWorkingHours] = useState(DEFAULT_WORKING_HOURS);
  /* Step 5 */
  const [contactEmail, setContactEmail] = useState("");
  const [dialCountry, setDialCountry] = useState("AE");
  const [phone, setPhone] = useState("");

  /* Transcribed: the old page pushes the fetched company into the search slice
     so downstream screens see the same selection. */
  useEffect(() => {
    if (company) dispatch(setCompany(company));
  }, [dispatch, company]);

  /* Entered from an existing profile, so the name is the company's, not the
     user's to invent. Prefills once the record arrives. */
  useEffect(() => {
    if (companyName) return;
    /* Either source may be the one that holds it: a claim on a directory
       record arrives with the fetched company, a hand-typed business arrives
       with only what the previous screen stored. */
    const fromFetch =
      company?.registered_company_name || company?.data?.registered_company_name;
    const next = fromFetch || filledCompany?.registered_company_name || "";
    if (next) setCompanyName(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, filledCompany]);

  /* Seed the address from whatever the profile already holds, so a claim on a
     company with an address on file does not retype it. */
  useEffect(() => {
    const existing = company?.address || company?.data?.address;
    if (!existing) return;
    setAddress((prev) =>
      prev === EMPTY_ADDRESS
        ? {
            line1: existing.address_line_1 || "",
            line2: existing.address_line_2 || "",
            area: existing.area || "",
            city: existing.city || "",
            postalCode: existing.postal_code || "",
          }
        : prev
    );
    if (existing.working_hours) setWorkingHours(parseWorkingHours(existing.working_hours));
  }, [company]);

  /* When step 1 is the search, the country picked there is the live answer —
     it must drive the state list on step 2 straight away, before anything has
     been written to the claim slice. Otherwise fall back to the record. */
  const countryCode =
    (searchable && search.countryCode) ||
    company?.country_code ||
    company?.data?.country_code ||
    filledCompany?.country_code ||
    "AE";

  /* ── The licence cascade ──────────────────────────────────────────────── Transcribed from TradeLicense: the UAE has its own authority map, and any other... */
  const [foreignStates, setForeignStates] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (!countryCode || countryCode === "AE") {
      setForeignStates([]);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      const states = (await getStatesByCountryCode(countryCode)) ?? [];
      if (!cancelled) setForeignStates(states.map((st) => st?.name).filter(Boolean));
    })();
    return () => {
      cancelled = true;
    };
  }, [countryCode]);

  const isUae = countryCode === "AE";

  const stateOptions = useMemo(
    () => (isUae ? Object.keys(LicensingAuthorityStateWiseDetails) : foreignStates),
    [isUae, foreignStates]
  );

  /* The three-level cascade, transcribed from TradeLicense.jsx:72-88. */
  const jurisdictionOptions = useMemo(() => {
    if (!isUae || !licenseState) return [];
    return Object.keys(LicensingAuthorityStateWiseDetails?.[licenseState] || {});
  }, [isUae, licenseState]);

  const registrationBodyOptions = useMemo(() => {
    if (!isUae || !licenseState || !jurisdiction) return [];
    return (LicensingAuthorityStateWiseDetails?.[licenseState]?.[jurisdiction] || []).filter(
      Boolean
    );
  }, [isUae, licenseState, jurisdiction]);

  /* Transcribed clearing rules (clearValue): a new state strands BOTH the
     jurisdiction and the body chosen under the old one; a new jurisdiction
     strands the body alone. */
  const changeLicenseState = (next) => {
    setLicenseState(next);
    setJurisdiction("");
    setRegistrationBody("");
  };

  const changeJurisdiction = (next) => {
    setJurisdiction(next);
    setRegistrationBody("");
  };

  /* The emirate list does not apply once the country is not the UAE. */
  useEffect(() => {
    if (licenseState && !stateOptions.includes(licenseState)) {
      setLicenseState("");
      setJurisdiction("");
      setRegistrationBody("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateOptions]);

  /* extractCountryCodeAndPhoneCode returns { iso2, phoneCode }. */
  const dialCodeOptions = useMemo(
    () =>
      (extractCountryCodeAndPhoneCode() || []).map((c) => ({
        value: c.iso2,
        label: `${c.iso2} (${c.phoneCode})`,
        phoneCode: c.phoneCode,
      })),
    []
  );

  const dialCode = useMemo(
    () => dialCodeOptions.find((o) => o.value === dialCountry)?.phoneCode || "+971",
    [dialCodeOptions, dialCountry]
  );

  const industryOptions = useMemo(
    () =>
      (subIndustries?.data || []).map((industry) => ({
        label: industry.sub_industry,
        value: industry.id,
      })),
    [subIndustries]
  );

  /* ── Validation ───────────────────────────────────────────────────────── Per-step, so Continue only gates the step in front of the user. */
  const stepValid = useMemo(() => {
    switch (step) {
      case 1:
        if (!confirmed) return false;
        if (!searchable) return companyName.trim().length >= 2;
        /* Searching: a directory match must actually be picked. Typing a name
           by hand needs only the name. */
        return search.notFound
          ? search.name.trim().length >= 2
          : Boolean(search.selectedKey);
      case 2:
        /* ICC-03: the licence is optional, so this step never blocks. The
           cascade is still offered, but a claim completes without any of it
           and the details are asked for again at the Verified application. */
        return true;
      case 3:
        return industries.length > 0;
      case 4:
        /* Line 1 and City only. Line 2 and Area are OPTIONAL — plenty of addresses have neither, and the payload already sends null for an empty one, so gating the... */
        /* State / Emirate is asked here, not on the licence step: that step
           is skippable now, and the API requires a non-blank address_state. */
        return Boolean(address.line1.trim() && address.city.trim() && licenseState);
      case 5:
        /* Count DIGITS, not characters. The submitted value strips everything
           non-numeric, so a length check over raw input would let "abcdef"
           through the gate and then post "+971" with no number behind it. */
        return Boolean(validateEmail(contactEmail) && digitsOf(phone).length >= 6);
      default:
        return true;
    }
  }, [
    step,
    confirmed,
    companyName,
    searchable,
    search.notFound,
    search.name,
    search.selectedKey,
    /* The licence fields are gone from this list with ICC-03: step 2 no
       longer gates on any of them, so nothing here reads them. */
    licenseState,
    industries,
    address,
    contactEmail,
    phone,
  ]);

  const email = userInfo?.email?.replace(/@/g, "=");

  /* ── Submit ─────────────────────────────────────────────────────────────
     The payload is transcribed field for field from ContactInformation. */
  const submit = async () => {
    setApiError("");
    const userSelectedCompany = JSON.parse(
      localStorage.getItem("userSelectedCompany") || "null"
    );
    /* Precedence transcribed from ContactInformation: the claim slice first —
       new_company means the user typed a business we do not hold, which the
       API takes as the literal "NEW_COMPANY" — then the stored selection. */
    const resolvedCompanyId = filledCompany?.new_company
      ? "NEW_COMPANY"
      : filledCompany
      ? filledCompany?.companyID || filledCompany?.wb_win
      : userSelectedCompany?.companyID ||
        userSelectedCompany?.data?.companyID ||
        userSelectedCompany?.data?.wb_win ||
        userSelectedCompany?.wb_win;

    try {
      const claimAddressDetails = {
        address_line_1: address.line1,
        address_line_2: address.line2 ? address.line2 : null,
        postal_code: address.postalCode,
        area: address.area ? address.area : null,
        city: address.city,
        state: licenseState,
        country: countryCode,
        working_hours: formatWorkingHours(workingHours),
      };
      dispatch(setClaimAddressDetails(claimAddressDetails));

      const response = await getCompanyClaimed({
        data: {
          companyID: resolvedCompanyId,
          /* Transcribed from ContactInformation: the claim slice is the source of record. */
          registered_company_name:
            filledCompany?.registered_company_name ||
            companyName ||
            search.name ||
            "",
          country_code: countryCode,
          trade_license: licenseFileUrl,
          license_no: licenseNo,
          /* The API indexes each entry as item['value'], so these must stay
             {label, value} objects. Sending bare ids raises a TypeError
             server-side and comes back as a 500. */
          sub_industry: industries,
          address_address_line_1: claimAddressDetails.address_line_1,
          address_city: claimAddressDetails.city,
          address_state: claimAddressDetails.state,
          address_country: claimAddressDetails.country,
          address_working_hours: claimAddressDetails.working_hours,
          // E.164, no separator — the original submits the phone input's own
          // value; its split("|") is defensive reading of legacy data, not a
          address_telephone: `${dialCode}${digitsOf(phone)}`,
          address_email: contactEmail,
          address_address_line_2: claimAddressDetails.address_line_2,
          address_postal_code: claimAddressDetails.postal_code,
          address_area: claimAddressDetails.area,
          registration_body: registrationBody,
        },
        token,
      });

      if (response?.data?.companyID) {
        setClaimedCompanyId(response.data.companyID);
        dispatch(setClearClaimDetails());
        localStorage.removeItem("userSelectedCompany");

        /* ICC-01 transfers ownership the moment the claim resolves, but the
           viewer's cached record still says they hold nothing: the profile
           they are about to open reads its role from userInfo, so it asked
           them to request access to the company they had just claimed. Refresh
           it here, before the exits are offered.

           Deliberately not fatal — the claim has already succeeded, so a
           failure here must not turn a completed claim into an error. The
           profile falls back to asking for access, as it does today. */
        try {
          const fresh = await getUserDetails(token).unwrap();
          if (fresh) dispatch(setUserInfo({ ...fresh }));
        } catch {
          /* keep the success screen; the record refreshes on next load */
        }

        setStep(SUBMITTED_STEP);
        return;
      }

      /* ICC-02 refuses a claim on a company that already has an owner and
         says so with `action: open_dispute_form`. Honour it: the user is
         here to claim, and the dispute form is what they can actually do.
         Reading `message` rather than the first value matters — the body
         leads with statusCode, so Object.values would show "409". */
      const payload = response?.error?.data;
      if (payload?.code === "already_claimed") {
        setBlockedAsClaimed(true);
        return;
      }
      setApiError(
        payload?.message ||
          (payload ? Object.values(payload)[0] : "") ||
          "Something went wrong. Please try again."
      );
    } catch (err) {
      /* Transcribed: a 401 here means the account is not permitted to claim
         this company, which is a different message from a generic failure. */
      if (err?.status === 401) {
        setApiError("You are not authorized to claim this page.");
      } else {
        setApiError(
          err?.data ? Object.values(err.data)[0] : "Something went wrong. Please try again."
        );
      }
    }
  };

  /* Uploading on leaving step 2 rather than at submit: it is the slow part,
     and doing it here means the last step is a single request. */
  const uploadLicenceIfNeeded = async () => {
    // Returns { ok, url }. The URL is handed back rather than read from state
    // on the next line: setState is async, so licenseFileUrl would still hold
    // the previous value (null on the first pass) and the claim would be
    // submitted with no licence attached.
    if (!licenseFile) return { ok: true, url: licenseFileUrl };
    try {
      setUploading(true);
      const uploaded = await uploadFileToS3(
        uploadFilePath?.tradeLicense,
        licenseFile,
        token,
        company?.companyID || company?.wb_win || company?.data?.companyID,
        "trade_license_staging"
      );
      const url = uploaded?.url || null;
      setLicenseFileUrl(url);
      return { ok: true, url };
    } catch (err) {
      setApiError(
        err?.data ? Object.values(err.data)[0] : "The trade license could not be uploaded."
      );
      return { ok: false, url: null };
    } finally {
      setUploading(false);
    }
  };

  const next = async () => {
    if (!stepValid) return;
    setApiError("");

    if (step === 1 && searchable) {
      /* The search hook owns this: it links an existing company to the
         individual, or writes a hand-typed one as filledCompany with
         new_company 1. Either way it calls back to advance. */
      if (search.notFound) search.saveNewBusiness();
      else await search.save();
      return;
    }

    if (step === 1) {
      dispatch(
        setFilledCompany({
          /* Merge onto what the previous screen stored. Spreading only the
             fetched company wiped new_company / wb_win on the manual path,
             and the claim then submitted with no companyID at all. */
          ...(filledCompany || {}),
          ...(company?.data || company || {}),
          // What the user typed on this step wins: it is the edit being saved.
          registered_company_name: companyName || filledCompany?.registered_company_name || "",
          country_code: countryCode,
        })
      );
      ga("Filled Company Details", `Filled Company Details; "${email}"`);
      setStep(2);
      return;
    }

    if (step === 2) {
      const { ok, url } = await uploadLicenceIfNeeded();
      if (!ok) return;
      dispatch(
        setClaimTradeLicenseDetails({
          trade_license: url,
          license_no: licenseNo,
          state: licenseState,
          selectedRegistrationBody: { value: registrationBody, label: registrationBody },
        })
      );
      ga(
        "Filled Company Trade License Details",
        `Filled Company Trade License Details; "${email}"`
      );
      setStep(3);
      return;
    }

    if (step === 3) {
      dispatch(setIndustryDetails(industries));
      ga("Filled industry Details", `Filled industry Details; "${email}"`);
      setStep(4);
      return;
    }

    if (step === 4) {
      ga(
        "Filled Company Address Details",
        `Filled Company Address Details; "${email}"`
      );
      setStep(5);
      return;
    }

    if (step === 5) await submit();
  };

  /* "Skip for now" — leaves the licence out entirely rather than carrying a
     half-filled cascade forward, so nothing partial reaches the claim or the
     Verified application built from it. The emirate is NOT cleared: step 4
     needs it, and it is editable there. */
  const skipLicense = () => {
    setApiError("");
    setLicenseNo("");
    setJurisdiction("");
    setRegistrationBody("");
    setLicenseFile(null);
    setLicenseFileUrl(null);
    ga("Skipped Company Trade License Details", `Skipped Company Trade License Details; "${email}"`);
    setStep(3);
  };

  const back = () => {
    if (step <= 1) return;
    const from = {
      2: ["Moved Back from Company Trade License Details", 1],
      3: ["Moved back from sector", 2],
      4: ["Moved back from company address", 3],
      5: ["Moved back from Contact Details", 4],
    }[step];
    if (from) {
      ga(from[0], `${from[0]}; "${email}"`);
      setStep(from[1]);
    }
  };

  return {
    step,
    steps: CLAIM_STEPS,
    totalSteps: TOTAL_STEPS,
    isSubmitted: step === SUBMITTED_STEP,
    /* Where the claimed profile actually lives. Falls back to the route's id
       for a company that was already a TREVEX one. */
    claimedCompanyId,
    /* A business the user typed by hand has no profile to view, so its
       confirmation offers no exits and the page moves on by itself. A claim on
       a listed company keeps the two buttons. */
    isNewCompany: Boolean(filledCompany?.new_company),
    stepValid,
    next,
    back,
    skipLicense,

    companyLoading,
    industriesLoading,
    isBusy: submitting || uploading,
    apiError,
    blockedAsClaimed,
    errors,
    setErrors,

    searchable,
    search,
    company,
    companyName,
    setCompanyName,
    nameLocked: Boolean(
      company?.registered_company_name || company?.data?.registered_company_name
    ),
    confirmed,
    setConfirmed,
    countryCode,

    licenseNo,
    setLicenseNo,
    licenseState,
    changeLicenseState,
    stateOptions,
    isUae,
    jurisdiction,
    changeJurisdiction,
    jurisdictionOptions,
    registrationBody,
    setRegistrationBody,
    registrationBodyOptions,
    licenseFile,
    setLicenseFile,

    industries,
    setIndustries,
    industryOptions,

    address,
    setAddressField: (key, value) => setAddress((a) => ({ ...a, [key]: value })),
    workingHours,
    setWorkingHours,

    contactEmail,
    setContactEmail,
    dialCountry,
    setDialCountry,
    dialCode,
    dialCodeOptions,
    phone,
    setPhone,
  };
}
